import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ToolTip } from '@repo/common';
import { ReactComponent as FolderIcon } from '@repo/common/assets/icons/20px/icon_20px_folder_outline.svg';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';
import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/20px/icon_20px_download.svg';
import { getBoardResources } from '../../api/twoDays/board.api';
import TdButton from '../../components/TwoDays/Button';
import { cn } from '../../components/TwoDays/model/derive';
import { MOCK_RESOURCES } from '../../components/TwoDays/model/mockResources';
import {
  GROUPS,
  ROLES,
  ROLE_OF_JOB,
  filesOf,
  isAllRoles,
  metaOf,
  sizeLabel,
  typeOf,
  pilotRoles,
  rolesLabel,
} from '../../components/TwoDays/model/teamFiles';
import { useWorkboardCtx } from '../../components/TwoDays/model/useWorkboard';
import { UpdatedChip } from '../../components/TwoDays/ops/TeamFiles';

/**
 * Resources (W11) — 맨 위 Scenario 카드(전체 화면 읽기 전용 페이지), 아래로 운영진이 Ops › Brief › Team files 에 올린
 * 파일·링크를 그룹(Background · Data and guides · Templates)별로.
 * 역할 필터(All roles · My role · 역할별)는 사용자마다 기억한다. 시작 뒤 바뀐 파일에는 "Updated · …".
 */

const filterKey = (viewerId) => `2days-resources-role:${viewerId ?? 'me'}`;
const loadFilter = (viewerId) => {
  try {
    return window.localStorage.getItem(filterKey(viewerId)) || 'all';
  } catch {
    return 'all';
  }
};
const saveFilter = (viewerId, value) => {
  try {
    window.localStorage.setItem(filterKey(viewerId), value);
  } catch {
    // 저장 못 해도 이번 화면에서는 그대로 쓴다
  }
};

/**
 * 파일 받기 — 아이콘만 있는 둥근 버튼 (36px · 모바일 44px).
 * 마우스를 올리거나 키보드로 오면 왼쪽에 "Download" 툴팁. 모바일은 hover 가 없어 키보드 포커스 때만 (탭하면 바로 받는다)
 */
const DownloadButton = ({ url, name }) => {
  const label = `Download ${name}`;
  const box =
    'flex h-11 w-11 items-center justify-center rounded-full bg-green-deep03/[0.08] transition-[background-color,transform] duration-150 sm:h-9 sm:w-9';
  if (!url) {
    return (
      <span aria-disabled='true' aria-label={label} className={cn(box, 'cursor-not-allowed opacity-40')}>
        <DownloadIcon aria-hidden className='h-5 w-5 text-icon-primary' />
      </span>
    );
  }
  return (
    <span className='relative inline-flex'>
      <a
        href={url}
        aria-label={label}
        className={cn(
          box,
          'peer text-icon-primary outline-none hover:bg-green-deep03/[0.14] focus-visible:bg-green-deep03/[0.14] focus-visible:shadow-[0_0_0_2px_#1dc37b] active:scale-[0.97]',
        )}
      >
        <DownloadIcon aria-hidden className='h-5 w-5' />
      </a>
      <span
        role='tooltip'
        className='pointer-events-none absolute right-[calc(100%+10px)] top-1/2 z-30 -translate-y-1/2 opacity-0 transition-opacity duration-150 peer-focus-visible:opacity-100 [@media(hover:hover)]:peer-hover:opacity-100'
      >
        <ToolTip position='right' state='accent' label='Download' />
      </span>
    </span>
  );
};

/** 받는 버튼 — 파일은 Download 아이콘, 링크는 새 탭에서 Open (글자) */
const Action = ({ file, name }) => {
  const isLink = file.kind === 'link';
  if (!isLink) return <DownloadButton url={file.url} name={name ?? file.name ?? 'file'} />;
  const label = 'Open';
  if (!file.url) {
    return (
      <TdButton variant='soft' size='s' disabled className='h-11 px-4 sm:h-9'>
        {label}
      </TdButton>
    );
  }
  return (
    <TdButton
      as='a'
      href={file.url}
      target={isLink ? '_blank' : undefined}
      rel={isLink ? 'noopener noreferrer' : undefined}
      variant='soft'
      size='s'
      className='h-11 px-4 sm:h-9'
    >
      {label}
    </TdButton>
  );
};

const RoleChip = ({ children }) => (
  <span className='inline-flex h-[22px] items-center rounded-[1px] bg-grayscale-gray01 px-2 text-[12px] font-medium text-text-tertiary'>
    {children}
  </span>
);

const ROW_COLS =
  'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_168px_104px] lg:grid-cols-[minmax(0,1fr)_240px_112px]';

/** 파일이 여럿인 항목 — 펼치면 하나씩 받는다 */
const FilesToggle = ({ count, open, onClick }) => (
  <TdButton
    variant='soft'
    size='s'
    aria-expanded={open}
    onClick={onClick}
    className='h-11 gap-1 px-4 sm:h-9'
  >
    {count} files
    <ChevronIcon aria-hidden className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
  </TdButton>
);

const Row = ({ file }) => {
  const all = isAllRoles(file.roles);
  const files = filesOf(file);
  const many = files.length > 1;
  const [open, setOpen] = useState(false);
  return (
    <li className='transition-colors hover:bg-green-deep03/[0.03]'>
      <div className={cn(ROW_COLS, 'px-4 py-3 sm:px-5')}>
        <span className='flex min-w-0 flex-col gap-0.5'>
          <span className='flex flex-wrap items-center gap-x-2 gap-y-1'>
            <span className='text-[15px] font-medium leading-[140%] text-text-primary [overflow-wrap:anywhere]'>
              {file.name}
            </span>
            {file.updatedAt && (
              <span className='hidden sm:inline-flex'>
                <UpdatedChip at={file.updatedAt} />
              </span>
            )}
          </span>
          <span className='text-[13px] leading-[140%] text-text-secondary'>
            {metaOf(file)}
            {/* 모바일은 역할을 메타 뒤에 글자로 */}
            <span className='sm:hidden'> · {rolesLabel(file.roles)}</span>
          </span>
          {file.updatedAt && (
            <span className='mt-1 sm:hidden'>
              <UpdatedChip at={file.updatedAt} />
            </span>
          )}
        </span>
        <span className='hidden flex-wrap gap-1 sm:flex'>
          {all ? (
            <RoleChip>All roles</RoleChip>
          ) : (
            pilotRoles(file.roles).map((r) => <RoleChip key={r}>{r}</RoleChip>)
          )}
        </span>
        <span className='flex justify-end'>
          {many ? (
            <FilesToggle count={files.length} open={open} onClick={() => setOpen((v) => !v)} />
          ) : (
            <Action file={file} />
          )}
        </span>
      </div>
      {many && open && (
        <ul className='flex flex-col gap-1 px-4 pb-3 sm:px-5'>
          {files.map((x) => (
            <li
              key={x.key ?? x.fileName}
              className='flex items-center justify-between gap-3 rounded-lg bg-grayscale-gray01 px-3 py-2'
            >
              <span className='flex min-w-0 flex-col'>
                <span className='truncate text-[14px] text-text-primary'>{x.fileName}</span>
                <span className='text-[12px] text-text-secondary'>
                  {[typeOf(x.fileName), sizeLabel(x.size)].filter(Boolean).join(' · ')}
                </span>
              </span>
              <Action file={{ kind: 'file', url: x.url }} name={x.fileName} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

/** 역할 필터 — 데스크톱은 팝오버, 모바일은 바텀시트 */
const RoleFilter = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const current = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!e.target.closest?.('[data-respick]')) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const list = (
    <ul role='listbox' aria-label='Show files for' className='flex flex-col'>
      {options.map((o) => {
        const on = o.value === current.value;
        return (
          <li key={o.value}>
            <button
              type='button'
              role='option'
              aria-selected={on}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={cn(
                'flex h-11 w-full items-center justify-between gap-3 rounded-lg px-3 text-left text-[14px] sm:h-9',
                on
                  ? 'bg-surface-green10 font-medium text-text-primary'
                  : 'text-text-primary hover:bg-grayscale-gray01',
              )}
            >
              {o.label}
              {on && <CheckIcon aria-hidden className='h-4 w-4 text-green-deep02' />}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div ref={wrapRef} data-respick className='relative'>
      <button
        type='button'
        aria-haspopup='listbox'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-11 items-center gap-1.5 rounded-full border bg-grayscale-gray00 px-4 text-[14px] sm:h-9',
          open ? 'border-green-deep02' : 'border-green-deep03/[0.12] hover:bg-grayscale-gray01',
        )}
      >
        <span className='text-text-secondary'>Show</span>
        <span className='font-medium text-text-primary'>{current.label}</span>
        <ChevronIcon
          aria-hidden
          className={cn('h-4 w-4 text-icon-secondary transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <>
          {/* 데스크톱 팝오버 */}
          <div className='absolute right-0 top-[calc(100%+6px)] z-30 hidden w-[220px] rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-1.5 shadow-popup-01 sm:block'>
            {list}
          </div>
          {/* 모바일 바텀시트 */}
          <div className='fixed inset-0 z-[1000] bg-grayscale-gray09/40 sm:hidden' aria-hidden />
          <div
            data-respick
            className='fixed inset-x-0 bottom-0 z-[1001] rounded-t-3xl bg-grayscale-gray00 px-4 pb-6 pt-4 shadow-popup-01 sm:hidden'
          >
            <p className='mb-2 px-3 text-[13px] text-text-secondary'>Show files for</p>
            {list}
          </div>
        </>
      )}
    </div>
  );
};

const Empty = ({ children }) => (
  <div className='flex flex-col items-center gap-3 rounded-xl border border-dashed border-green-deep03/[0.24] px-6 py-12 text-center'>
    <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-surface-green10'>
      <FolderIcon aria-hidden className='h-5 w-5 text-green-deep02' />
    </span>
    <p className='text-[15px] font-medium text-text-primary'>{children}</p>
  </div>
);

const TwoDaysResources = () => {
  const { live, slug, viewer, data, scenarioRevealed, scenarioOpensAt, openScenario } =
    useWorkboardCtx();
  const [files, setFiles] = useState(live ? null : MOCK_RESOURCES);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState(() => loadFilter(data.viewerId));

  // 받기 주소는 1시간짜리라 창으로 돌아오면 새로 받는다
  const load = useCallback(() => {
    if (!live || !slug) return;
    getBoardResources(slug)
      .then((list) => {
        setFiles(list ?? []);
        setError(false);
      })
      .catch(() => setError(true));
  }, [live, slug]);
  useEffect(() => {
    load();
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [load]);

  const myRole = ROLE_OF_JOB[viewer?.job];
  const options = [
    { value: 'all', label: 'All roles' },
    ...(myRole ? [{ value: 'mine', label: `My role · ${myRole}` }] : []),
    ...ROLES.filter((r) => r !== myRole).map((r) => ({ value: r, label: r })),
  ];
  const current = options.some((o) => o.value === filter) ? filter : 'all';
  const role = current === 'mine' ? myRole : current === 'all' ? null : current;
  const choose = (value) => {
    setFilter(value);
    saveFilter(data.viewerId, value);
  };

  const others = (files ?? []).filter((f) => !f.pinned);
  const shown = role ? others.filter((f) => f.roles.includes(role)) : others;

  return (
    <div className='flex max-w-[1080px] flex-col gap-6'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
        <div className='flex flex-col gap-1'>
          <h1
            data-coach='resources-title'
            className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'
          >
            Resources
          </h1>
          <p className='text-[15px] leading-[150%] text-text-secondary'>
            Files from the 2days team. Download anytime.
          </p>
        </div>
        <div className='self-start'>
          <RoleFilter value={current} options={options} onChange={choose} />
        </div>
      </div>

      {error && !files && (
        <p role='alert' className='text-[14px] text-functional-failed03'>
          Couldn’t load the files. Refresh the page.
        </p>
      )}
      {!files && !error && <p className='text-[14px] text-text-secondary'>Loading…</p>}

      {/* 시나리오 — 언제든 다시 여는 고정 진입점 (내 시작 시각 전에는 잠김) */}
      <div
        data-coach='resources-scenario'
        className='flex items-center justify-between gap-3 rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00 px-4 py-3.5 shadow-4 sm:px-5'
      >
        <span className='flex min-w-0 flex-col gap-0.5'>
          <span className='text-[15px] font-medium text-text-primary'>Scenario</span>
          <span className='truncate text-[13px] text-text-secondary'>
            {/* 시나리오 미리보기 — 줄바꿈 · 앞 글머리표를 빼고 한 줄로 (시작 전에는 열리는 시각) */}
            {scenarioRevealed
              ? data.project.scenario
                  ?.replace(/\s+/g, ' ')
                  .replace(/^[○●•·*-]\s*/, '')
                  .trim() || 'The situation your team works in'
              : `Opens at ${scenarioOpensAt}`}
          </span>
        </span>
        <TdButton
          variant='soft'
          size='s'
          disabled={!scenarioRevealed}
          onClick={openScenario}
          className='h-11 px-4 sm:h-9'
        >
          Open
        </TdButton>
      </div>

      {files &&
        (others.length === 0 ? (
          <Empty>No files yet</Empty>
        ) : shown.length === 0 ? (
          <Empty>No files for {current === 'mine' ? 'your role' : current} yet</Empty>
        ) : (
          GROUPS.map((g) => {
            const list = shown.filter((f) => f.group === g.key);
            if (list.length === 0) return null;
            return (
              <section key={g.key} aria-labelledby={`res-${g.key}`} className='flex flex-col gap-3'>
                <h2 id={`res-${g.key}`} className='text-[17px] font-medium text-text-primary'>
                  {g.label}
                </h2>
                <ul className='divide-y divide-green-deep03/[0.08] overflow-hidden rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00'>
                  {list.map((f) => (
                    <Row key={f.id} file={f} />
                  ))}
                </ul>
              </section>
            );
          })
        ))}
    </div>
  );
};

export default TwoDaysResources;
